import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, MapPin, Search, Truck, X } from 'lucide-react'
import { cn } from 'utils/cn'
import { useDebouncedValue } from 'hooks/useDebouncedValue'
import { useAnchoredPopover } from '../hooks/useAnchoredPopover'
import { type BoxTruckVerification, type DriverLastLocationDto, fetchMyBoxTruckDrivers } from '../services/boxTruckLoadsApi'
import { orderDriverPickerRows } from '../utils/orderDriverPickerRows'
import { canEditLocation, isSelectable } from '../utils/rowCapabilities'
import { DriverLocationEditor } from './DriverLocationEditor'

interface SearchForDriverSelectProps {
    /** The picked drivers shown in the trigger — CONTROLLED by the page, which keys them per search tab so a
     *  pick on one tab never bleeds into another. Empty = nothing picked. */
    selected: BoxTruckVerification[]
    /** Toggles a (locatable) driver in/out of the selection — the page rebuilds the origin + equipment draft
     *  from the whole selection. The operator applies that draft with the Search button. */
    onToggle: (driver: BoxTruckVerification) => void
    /** Clears the whole selection (the filters it prefilled stay as they are). */
    onClearSelected: () => void
    /** A location the operator corrected here. The page's persisted selection holds a SNAPSHOT of each picked
     *  driver, so it has to be patched too — patching only this dropdown's own rows leaves the stored copy on
     *  the pre-edit fix, and the load drawer rebuilds its pinned row from that. */
    onLocationSaved?: (userId: string, lastLocation: DriverLastLocationDto) => void
    /** Driver Bids needs an account-backed bidder id; lane search only needs a stored origin. */
    requireAppAccount?: boolean
    disabled?: boolean
}

/** How many of the operator's drivers to pull for the dropdown. Beyond this, typing narrows the (client-side) list. */
const DRIVER_FETCH_LIMIT = 200

const locationLabel = (d: BoxTruckVerification): string | null => {
    if (d.lastLocation) return [d.lastLocation.city, d.lastLocation.state].filter(Boolean).join(', ') || 'Location shared'
    return null
}

/** Multi-select driver picker that prefills lane origin/equipment from stored driver data. */
export const SearchForDriverSelect = ({
    selected,
    onToggle,
    onClearSelected,
    onLocationSaved,
    requireAppAccount = false,
    disabled = false,
}: SearchForDriverSelectProps) => {
    const { open, anchorRect, containerRef, triggerRef, panelRef, togglePanel, panelPositionStyle } = useAnchoredPopover()
    const [drivers, setDrivers] = useState<BoxTruckVerification[] | null>(null)
    const [loading, setLoading] = useState(false)
    const [editingDriverId, setEditingDriverId] = useState<string | null>(null)
    // Per-driver save counter, so a slower re-read from an earlier correction can't overwrite a later one.
    // A ref, not state: it must be readable and writable synchronously around an await, and bumping it must
    // never render. Bounded by the drivers an operator edits in one mounted picker.
    const saveSequenceRef = useRef(new Map<string, number>())
    // Keep request failures distinct from a genuinely empty fleet.
    const [fetchError, setFetchError] = useState(false)
    const [q, setQ] = useState('')
    // Freeze the selected-first group for one open session. New picks stay where they were clicked so the
    // operator can continue down the list; the next open captures the completed selection and pins it.
    const [selectedAtOpen, setSelectedAtOpen] = useState(selected)
    // Query the server because the initial page does not contain the whole fleet.
    const term = q.trim()
    const debouncedTerm = useDebouncedValue(term, 300)
    // Store the answered term with its rows so stale results can never be picked for a newer query.
    const [searchResults, setSearchResults] = useState<{ term: string; items: BoxTruckVerification[] } | null>(null)
    const [inFlight, setInFlight] = useState(false)
    const freshResults = term && searchResults?.term === term ? searchResults.items : null
    const searching = !!term && (term !== debouncedTerm || inFlight)

    // Lazy-load the operator's drivers the first time the panel opens.
    useEffect(() => {
        if (!open || drivers !== null || loading || fetchError) return
        setLoading(true)
        fetchMyBoxTruckDrivers(1, DRIVER_FETCH_LIMIT)
            .then((r) => setDrivers(r.items))
            .catch((error) => {
                console.error('[box-truck] drivers fetch for Search-for-driver failed', error)
                setFetchError(true)
            })
            .finally(() => setLoading(false))
    }, [open, drivers, loading, fetchError])

    // Fetch on the debounced term. The endpoint matches the fleet record's identity fields (claim
    // name / email / phone) — the fields the operator actually knows the driver by.
    useEffect(() => {
        if (!debouncedTerm) {
            setSearchResults(null)
            return undefined
        }
        let cancelled = false
        setInFlight(true)
        fetchMyBoxTruckDrivers(1, DRIVER_FETCH_LIMIT, debouncedTerm)
            .then((r) => {
                if (!cancelled) setSearchResults({ term: debouncedTerm, items: r.items })
            })
            .catch((err) => {
                // Degrade to the local sift below, but SAY SO — a 500/auth failure must stay
                // distinguishable from "no matches" in the logs.
                console.error('[SearchForDriverSelect] driver search failed, falling back to local filter', err)
                if (!cancelled) setSearchResults(null)
            })
            .finally(() => {
                if (!cancelled) setInFlight(false)
            })
        return () => {
            cancelled = true
            setInFlight(false)
        }
    }, [debouncedTerm])

    const filtered = useMemo(() => {
        const termLc = term.toLowerCase()
        // Include the registration name because the account name may be unset.
        const localSift = (rows: BoxTruckVerification[]) =>
            rows.filter(
                (d) =>
                    d.name?.toLowerCase().includes(termLc) ||
                    d.boxTruckInfo?.info?.name?.toLowerCase().includes(termLc) ||
                    d.email?.toLowerCase().includes(termLc) ||
                    d.boxTruckInfo?.info?.phone?.toLowerCase().includes(termLc)
            )
        // Merge server matches with the local registration-name sift.
        const merged = () => {
            const byId = new Map<string, BoxTruckVerification>()
            for (const d of [...localSift(selected), ...localSift(drivers ?? []), ...(freshResults ?? [])]) byId.set(d.userId, d)
            return [...byId.values()]
        }
        const rows = term ? merged() : (drivers ?? [])
        return orderDriverPickerRows(rows, selectedAtOpen, !term)
    }, [drivers, term, freshResults, selected, selectedAtOpen])

    const selectedIds = useMemo(() => new Set(selected.map((d) => d.userId)), [selected])
    const driverLabel = (d: BoxTruckVerification): string => d.boxTruckInfo?.info?.name || d.name || d.email
    const handlePanelToggle = () => {
        if (!open) setSelectedAtOpen(selected)
        togglePanel()
    }
    // Keep the panel open for multi-select.
    const toggle = (d: BoxTruckVerification) => {
        if (!isSelectable(d, requireAppAccount)) return
        onToggle(d)
    }
    const replaceDriver = (updated: BoxTruckVerification) => {
        const patchDriver = (driver: BoxTruckVerification): BoxTruckVerification => (driver.userId === updated.userId ? updated : driver)
        setDrivers((current) => current?.map(patchDriver) ?? current)
        setSearchResults((current) => (current ? { ...current, items: current.items.map(patchDriver) } : current))
        setSelectedAtOpen((current) => current.map(patchDriver))
    }
    const handleLocationSaved = async (driver: BoxTruckVerification, lastLocation: DriverLastLocationDto) => {
        // Claim this save as the newest for this driver BEFORE any await. The re-read below now feeds
        // PERSISTED state, so an out-of-order one no longer costs a transient wrong row — it writes a stale
        // fix to disk, which is the very failure this PR removes. Two quick corrections race exactly that
        // way: B's write lands, then A's earlier re-read resolves with the pre-B record and would push it
        // back into the store. A newer save makes this one stop at its own await.
        const saveSequence = (saveSequenceRef.current.get(driver.userId) ?? 0) + 1
        saveSequenceRef.current.set(driver.userId, saveSequence)
        const superseded = () => saveSequenceRef.current.get(driver.userId) !== saveSequence

        replaceDriver({ ...driver, lastLocation })
        onLocationSaved?.(driver.userId, lastLocation)
        if (!driver.claimDriverId) return
        try {
            const refreshed = await fetchMyBoxTruckDrivers(1, 1, undefined, { driverId: driver.claimDriverId })
            if (superseded()) return
            const current = refreshed.items.find(({ userId }) => userId === driver.userId)
            if (!current) return
            // A re-read carrying NO location cannot be more current than the write that just succeeded — the
            // likely cause is read-after-write lag. Keep the written fix in both surfaces rather than letting
            // this dropdown blank the location while the store (and the drawer's pinned row) still show it.
            const effectiveLocation = current.lastLocation ?? lastLocation
            // Otherwise the re-read is the server's own record and supersedes the write response everywhere
            // the write response went — the dropdown row and the page's stored selection alike.
            replaceDriver({ ...current, lastLocation: effectiveLocation })
            onLocationSaved?.(current.userId, effectiveLocation)
        } catch (error) {
            // The write succeeded and its response is already rendered; keep that good state if refresh fails.
            console.error('[box-truck] driver refresh after location update failed', error)
        }
    }
    const handleEditorOpenChange = (userId: string, editorOpen: boolean) =>
        setEditingDriverId((current) => (editorOpen ? userId : current === userId ? null : current))

    return (
        <div ref={containerRef} className="relative w-full">
            <button
                ref={triggerRef}
                type="button"
                disabled={disabled}
                onClick={handlePanelToggle}
                className={cn(
                    'relative flex h-9 w-full items-center gap-2 overflow-hidden rounded-xl border-none bg-surface pl-3 text-left shadow-field outline-none transition-[box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-accent',
                    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                )}
            >
                <Truck size={16} strokeWidth={1.5} className="shrink-0 text-foreground-muted" />
                <span
                    className={cn(
                        'flex-1 truncate whitespace-nowrap text-sm leading-5',
                        selected.length ? 'font-medium text-foreground' : 'text-foreground-muted'
                    )}
                >
                    {selected.length === 0
                        ? 'Search for driver'
                        : selected.length === 1
                          ? driverLabel(selected[0])
                          : `${driverLabel(selected[0])} +${selected.length - 1}`}
                </span>
                {selected.length > 0 && (
                    // A <span> with a click handler, not a nested <button> (invalid HTML inside the trigger
                    // button). Clears the whole selection's label — the filters it prefilled stay as they are,
                    // since the operator may have already edited them.
                    <span
                        role="button"
                        tabIndex={0}
                        aria-label="Clear selected drivers"
                        onClick={(e) => {
                            e.stopPropagation()
                            onClearSelected()
                        }}
                        onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return
                            e.preventDefault()
                            e.stopPropagation()
                            onClearSelected()
                        }}
                        className="flex h-9 shrink-0 cursor-pointer items-center justify-center px-1 text-foreground-muted hover:text-foreground"
                    >
                        <X size={14} strokeWidth={2} />
                    </span>
                )}
                <span className="flex h-9 shrink-0 items-center justify-center px-3 py-2 text-foreground">
                    <ChevronDown size={16} strokeWidth={1.5} className={cn('transition-transform duration-200', open ? 'rotate-180' : 'rotate-0')} />
                </span>
            </button>

            {open &&
                anchorRect &&
                createPortal(
                    <div
                        ref={panelRef}
                        style={{ ...panelPositionStyle, minWidth: 280, maxHeight: Math.min(360, window.innerHeight - 16) }}
                        className="flex flex-col rounded-3xl bg-surface p-1 shadow-overlay backdrop-blur-[20px]"
                    >
                        <div className="flex items-center gap-2 px-3 py-2">
                            <Search size={14} className="shrink-0 text-foreground-muted" />
                            <input
                                autoFocus
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Filter drivers…"
                                className="w-full border-none bg-transparent text-sm text-foreground outline-none placeholder:text-foreground-muted"
                            />
                        </div>
                        <div className="mx-3 h-px bg-border" />
                        <div className="flex flex-col gap-0.5 overflow-y-auto p-1" style={{ maxHeight: 300 }}>
                            {loading && <div className="px-3 py-4 text-center text-sm text-foreground-muted">Loading drivers…</div>}
                            {!loading && fetchError && (
                                <div className="px-3 py-4 text-center">
                                    <p className="text-sm font-medium text-danger">Couldn’t load your drivers</p>
                                    <button
                                        type="button"
                                        onClick={() => setFetchError(false)}
                                        className="mt-1 cursor-pointer border-none bg-transparent text-sm font-medium text-accent hover:underline"
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}
                            {!loading && !fetchError && searching && filtered.length === 0 && (
                                <div className="px-3 py-4 text-center text-xs text-foreground-muted">Searching…</div>
                            )}
                            {!loading && !fetchError && !searching && filtered.length === 0 && (
                                <div className="px-3 py-4 text-center text-sm text-foreground-muted">No drivers found</div>
                            )}
                            {!loading &&
                                !fetchError &&
                                filtered.map((d) => {
                                    const loc = locationLabel(d)
                                    const selectable = isSelectable(d, requireAppAccount)
                                    const checked = selectedIds.has(d.userId)
                                    return (
                                        <div
                                            key={d.userId}
                                            className={cn(
                                                'flex w-full items-start rounded-[20px] transition-colors duration-100',
                                                editingDriverId === d.userId ? 'bg-accent-soft' : selectable ? 'hover:bg-default' : ''
                                            )}
                                        >
                                            <button
                                                type="button"
                                                disabled={!selectable}
                                                onClick={() => toggle(d)}
                                                className={cn(
                                                    'flex min-w-0 flex-1 items-start gap-2.5 border-none bg-transparent px-3 py-2 text-left',
                                                    selectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                                                )}
                                            >
                                                <span
                                                    className={cn(
                                                        'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-[5px] border transition-colors',
                                                        checked ? 'border-accent bg-accent text-background' : 'border-border bg-surface'
                                                    )}
                                                >
                                                    {checked && <Check size={12} strokeWidth={2.5} />}
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-medium leading-snug text-foreground">{driverLabel(d)}</div>
                                                    <div className="mt-0.5 flex items-center gap-1 text-xs text-foreground-muted">
                                                        <MapPin size={11} className="shrink-0" />
                                                        <span className="truncate">{loc ?? 'Set location to find loads'}</span>
                                                    </div>
                                                </div>
                                            </button>
                                            {canEditLocation(d) && (
                                                <div className="mr-2 shrink-0 self-center">
                                                    <DriverLocationEditor
                                                        userId={d.userId}
                                                        onSaved={(location) => handleLocationSaved(d, location)}
                                                        onOpenChange={(editorOpen) => handleEditorOpenChange(d.userId, editorOpen)}
                                                        small={false}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    )
}
